/* eslint-disable testing-library/no-debugging-utils */
import { getEnrollmentyHYCSPlaidToken, useSubmitEnrollmentHYCSFundAccountData } from '@egw/api'
import { SubmitEnrollmentErrorTypes } from '@egw/api/src/models/enrollment/generic/errors'
import { Alert, AlertType, Button, ButtonVariant, IconConfirmationCircle, IconErrorCircle } from '@egw/components'
import { sendEvent } from '@egw/events'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { type Dispatch, type FC, type SetStateAction, useCallback, useEffect, useState } from 'react'
import {
  type PlaidLinkError,
  type PlaidLinkOnEvent,
  type PlaidLinkOnEventMetadata,
  type PlaidLinkOnExit,
  type PlaidLinkOnExitMetadata,
  type PlaidLinkOnSuccess,
  type PlaidLinkOnSuccessMetadata,
  usePlaidLink,
} from 'react-plaid-link'
import { EDataLayerEventWealth } from '../../constants/events/EDataLayerEventWealth'
import { LinkingType } from '../../types/account-type'
import { Loader } from '../loader/loader'

const log = new Logger('Instant Linking - Plaid')

export interface PlaidLinkProps {
  setLinkingType: Dispatch<SetStateAction<LinkingType>>
  onPlaidSuccess: Dispatch<SetStateAction<string | null>>
  onClose: () => void
  applicationId: string | null | undefined
}

export const PlaidLink: FC<PlaidLinkProps> = ({ onPlaidSuccess, onClose, setLinkingType, applicationId }) => {
  const [isCreatingLinkToken, setIsCreatingLinkToken] = useState(false)
  const [isCreatingLinkTokenError, setIsCreatingLinkTokenError] = useState(false)
  const [step, setStep] = useState(0)
  const [linkToken, setLinkToken] = useState<string | null>(null)
  const [plaidAchAccountNickname, setPlaidAchAccountNickname] = useState<string | null>(null)
  const [linkingError, setLinkingError] = useState(false)
  const [linkingSuccess, setLinkingSuccess] = useState(false)
  const { t } = useTranslation('features-enrollment')

  useEffect(() => {
    // Reset the state when the component unmounts
    const unsubscribe = (): void => {
      setStep(0)
      setLinkingSuccess(false)
      setLinkingError(false)
      setIsCreatingLinkTokenError(false)
      setIsCreatingLinkToken(false)
    }

    return () => {
      unsubscribe()
    }
  }, [])

  const {
    mutate: linkPlaidAchAccount,
    isLoading: isLinkPlaidAchAccountLoading,
    error: linkPlaidAchAccountError,
  } = useSubmitEnrollmentHYCSFundAccountData()

  // Create Plaid Link token on component mount
  useEffect(() => {
    const createLinkToken = async (): Promise<void> => {
      setIsCreatingLinkToken(true)
      try {
        const linkTokenResponse = await getEnrollmentyHYCSPlaidToken({
          createPlaidLinkToken: true,
        })
        if (
          linkTokenResponse.__typename === 'SubmitEnrollmentSuccess' &&
          linkTokenResponse.enrollmentData?.plaidLinkToken
        ) {
          sendEvent(EDataLayerEventWealth.BANKHYCHYS_PLAID_LINK_TOKEN_GENERATE_SUCCESS, { applicationId })
          setLinkToken(linkTokenResponse.enrollmentData.plaidLinkToken)
        } else {
          sendEvent(EDataLayerEventWealth.BANKHYCHYS_PLAID_LINK_TOKEN_GENERATE_FAIL, { applicationId })
          setIsCreatingLinkTokenError(true)
        }
      } catch (error) {
        log.error('Error creating Plaid Link token: ', error)
        sendEvent(EDataLayerEventWealth.BANKHYCHYS_PLAID_LINK_TOKEN_GENERATE_FAIL, { applicationId })
        setIsCreatingLinkTokenError(true)
      } finally {
        setIsCreatingLinkToken(false)
      }
    }
    void createLinkToken()
  }, [applicationId])

  const handlePlaidLinkSuccess: PlaidLinkOnSuccess = useCallback(
    (publicToken: string, metadata: PlaidLinkOnSuccessMetadata) => {
      sendEvent(EDataLayerEventWealth.BANKHYCHYS_PLAID_LINK_SUCCESS, { applicationId })
      log.debug('Plaid linking success')
      setLinkingType(LinkingType.PLAID_INSTANT)
      setPlaidAchAccountNickname(metadata.accounts[0].name)

      setStep(1)

      try {
        void linkPlaidAchAccount({
          linkPlaid: {
            publicToken,
            accountName: metadata.accounts[0].name,
            metadata: {
              institutionId: metadata.institution?.institution_id ?? '',
              institutionName: metadata.institution?.name ?? '',
              accounts: [
                {
                  accountId: metadata.accounts[0].id,
                  name: metadata.accounts[0].name,
                  mask: metadata.accounts[0].mask,
                  type: metadata.accounts[0].type,
                  subtype: metadata.accounts[0].subtype,
                },
              ],
            },
          },
        }).then((res) => {
          // If the account is not linked, set the linking error state
          if (
            res?.__typename === 'SubmitEnrollmentError' &&
            res.type === SubmitEnrollmentErrorTypes.LINKED_ACCOUNT_FAILED
          ) {
            setLinkingError(true)
            sendEvent('eg_addManualAccount_fail', {
              eventSource: 'egWealth',
              category: 'manual-link',
              subCategory: 'result',
              failureReason: res.type,
            })
            return
          }
          // If the account is linked, set the linking success state

          if (res?.__typename === 'SubmitEnrollmentSuccess' && res.enrollmentData?.accountLinked) {
            setLinkingSuccess(true)
            onPlaidSuccess(metadata.accounts[0].name)
          }
        })
      } catch (error) {
        log.error(error)
      }
      // SEND PLAID LINK SUCCESSFUL EVENT HERE
      sendEvent('bankhychys_core_plaidLink_success')
    },
    [applicationId, linkPlaidAchAccount, onPlaidSuccess, setLinkingType],
  )

  const handlePlaidLinkExit: PlaidLinkOnExit = useCallback(
    (error: PlaidLinkError | null, metadata: PlaidLinkOnExitMetadata) => {
      log.debug('Plaid linking exit', { error, metadata })
      sendEvent(EDataLayerEventWealth.BANKHYCHYS_PLAID_LINK_EXIT, { applicationId })
    },
    [applicationId],
  )

  const handlePlaidLinkEvent: PlaidLinkOnEvent = useCallback(
    (eventName, metadata: PlaidLinkOnEventMetadata) => {
      log.debug('Plaid linking event', { eventName, metadata })
      sendEvent(EDataLayerEventWealth.BANKHYCHYS_PLAID_LINK_EVENT, { applicationId })
    },
    [applicationId],
  )

  const { open, ready, exit } = usePlaidLink({
    onSuccess: handlePlaidLinkSuccess,
    onExit: handlePlaidLinkExit,
    onEvent: handlePlaidLinkEvent,
    token: linkToken,
  })

  useEffect(() => {
    const unsubscribe = (): void => {
      exit()
    }

    return () => {
      unsubscribe()
    }
  }, [exit])

  return (
    <>
      {/* Instant or Manual linking screen */}
      {step === 0 && (
        <>
          <p className="font-semibold mb-3xs mt-xs">{t('accounts.instant.title')}</p>
          <p className="mb-xs text-body">{t('accounts.instant.message')}</p>
          {isCreatingLinkTokenError ? (
            <Alert
              alertType={AlertType.Error}
              title={t('plaid_link.token_error_header')}
              description={t('plaid_link.token_error_message')}
              className="mb-2xs"
            />
          ) : null}
          <Button
            variant={ButtonVariant.Secondary}
            onClick={() => {
              sendEvent(EDataLayerEventWealth.BANKHYCHYS_FUND_ACCOUNT_PLAID_LINK_CLICKED)
              sendEvent(EDataLayerEventWealth.BANKHYCHYS_LINKINSTANTLY_INTENT)
              open()
            }}
            testId="add_account_link_instantly_secondary_btn"
            id="add_account_link_instantly_secondary_btn"
            isLoading={isCreatingLinkToken}
            isDisabled={isCreatingLinkTokenError || !ready}
          >
            {t('accounts.instant.cta')}
          </Button>
        </>
      )}

      {/* Update nickname form has been removed from the enrollment flow */}

      {/* Loading, error, success states for saving a new account */}
      {step === 1 && (
        <>
          {isLinkPlaidAchAccountLoading ? (
            <>
              <Loader className="my-lg" />
              <p className="font-light text-header text-xl mb-2xs">{t('plaid_link.loading_title')}</p>
              <p>{t('plaid_link.loading_message')}</p>
            </>
          ) : null}

          {linkingSuccess ? (
            <>
              <IconConfirmationCircle className="mt-lg mb-sm" />
              <p className="mb-xs">
                {t('plaid_link.account_verified', {
                  accountName: plaidAchAccountNickname,
                })}
              </p>
              <Button
                variant={ButtonVariant.Primary}
                onClick={() => {
                  onClose()
                }}
                className="mb-2xs"
                testId="add_account_success_continue_primary_btn"
              >
                {t('plaid_link.done')}
              </Button>
            </>
          ) : null}

          {linkPlaidAchAccountError || linkingError ? (
            <>
              <IconErrorCircle className="my-lg" />
              <p className="mb-xs">{t('plaid_link.error_p1')}</p>
              <p className="mb-xs">{t('plaid_link.error_p2')}</p>
              <Button
                variant={ButtonVariant.Primary}
                onClick={() => {
                  // TODO: Switch to manual linking
                }}
                className="mb-2xs"
                testId="add_account_error_link_manually_primary_btn"
              >
                {t('plaid_link.add_manually')}
              </Button>
              <Button
                variant={ButtonVariant.Secondary}
                onClick={() => {
                  onClose()
                }}
                className="mb-2xs"
                testId="plaid-cancel_button"
              >
                {t('plaid_link.cancel')}
              </Button>
            </>
          ) : null}
        </>
      )}
    </>
  )
}
